import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Loader2,
  CheckCircle2,
  AlertCircle,
  GitBranch,
  Eye,
  FileText,
  MessageCircle,
  BarChart3,
  ChevronDown,
} from "lucide-react";
import type { FeedbackItem } from "../../stores/feedback.js";
import { useFeedbackStore } from "../../stores/feedback.js";
import { useRespondToInput } from "../../api/hooks.js";

const TYPE_CONFIG: Record<
  string,
  { icon: any; color: string; borderColor: string; label: string }
> = {
  progress: {
    icon: Loader2,
    color: "text-brand-cyan",
    borderColor: "border-l-brand-cyan",
    label: "Progress",
  },
  completion: {
    icon: CheckCircle2,
    color: "text-brand-green",
    borderColor: "border-l-brand-green",
    label: "Complete",
  },
  error: {
    icon: AlertCircle,
    color: "text-brand-pink",
    borderColor: "border-l-brand-pink",
    label: "Error",
  },
  decision: {
    icon: GitBranch,
    color: "text-brand-purple",
    borderColor: "border-l-brand-purple",
    label: "Decision",
  },
  observation: {
    icon: Eye,
    color: "text-brand-cyan",
    borderColor: "border-l-brand-cyan",
    label: "Observation",
  },
  artifact: {
    icon: FileText,
    color: "text-yellow-400",
    borderColor: "border-l-yellow-400",
    label: "Artifact",
  },
  "request-for-input": {
    icon: MessageCircle,
    color: "text-amber-400",
    borderColor: "border-l-amber-400",
    label: "Input Required",
  },
  confidence: {
    icon: BarChart3,
    color: "text-text-secondary",
    borderColor: "border-l-border-accent",
    label: "Confidence",
  },
};

interface FeedbackMessageProps {
  msg: FeedbackItem;
  compact?: boolean;
}

export function FeedbackMessage({ msg, compact }: FeedbackMessageProps) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[msg.type] ?? TYPE_CONFIG.progress;
  const Icon = config.icon;
  const payload = msg.payload ?? {};
  const time = new Date(msg.timestamp).toLocaleTimeString();

  const primaryContent = extractPrimaryContent(payload, msg.type);
  const hasExpandableContent = hasMoreContent(payload, msg.type);
  const isInputRequest = msg.type === "request-for-input";

  if (compact) {
    return (
      <div className="flex gap-3 py-1 px-4 hover:bg-surface-lighter/5 transition-colors text-xs">
        <span className="text-text-muted shrink-0 font-mono w-16 tabular-nums">{time}</span>
        <Icon className={`w-3 h-3 ${config.color} shrink-0 mt-0.5`} />
        <span className="text-text-secondary shrink-0 w-24 truncate">
          {msg.source?.name ?? "agent"}
        </span>
        <span className="text-text-primary truncate">{primaryContent}</span>
      </div>
    );
  }

  return (
    <div
      className={`border-l-2 ${config.borderColor} ${
        isInputRequest ? "bg-amber-400/[0.03]" : ""
      } transition-colors group`}
    >
      <button
        onClick={() => hasExpandableContent && setExpanded(!expanded)}
        className={`w-full text-left px-4 py-2.5 flex items-start gap-3 ${
          hasExpandableContent ? "cursor-pointer hover:bg-surface-lighter/5 active:scale-[0.98]" : "cursor-default"
        } transition-[color,background-color,transform] duration-200`}
      >
        {/* Icon */}
        <div className={`mt-0.5 shrink-0 ${config.color}`}>
          <Icon
            className={`w-4 h-4 ${
              msg.type === "progress" ? "animate-spin" : ""
            }`}
          />
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-0.5">
            <span className="text-xs font-medium text-text-primary">
              {msg.source?.name ?? "agent"}
            </span>
            <span className="text-[10px] text-text-muted font-mono tabular-nums">{time}</span>
            {msg.type === "confidence" && payload.score != null && (
              <div className="flex items-center gap-1">
                <div className="w-12 h-1 bg-surface-lighter rounded-full overflow-hidden">
                  <div
                    className="h-full bg-brand-purple rounded-full"
                    style={{ width: `${Math.round(payload.score * 100)}%` }}
                  />
                </div>
                <span className="text-[10px] text-text-muted tabular-nums">
                  {Math.round(payload.score * 100)}%
                </span>
              </div>
            )}
            {msg.type === "observation" && payload.importance != null && (
              <div className="flex gap-0.5">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-1 h-1 rounded-full ${
                      i < payload.importance ? "bg-brand-cyan" : "bg-surface-lighter"
                    }`}
                  />
                ))}
              </div>
            )}
            {msg.type === "artifact" && payload.stage && (
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-yellow-400/10 text-yellow-400">
                {payload.stage}
              </span>
            )}
          </div>
          <p className="text-sm text-text-secondary leading-relaxed">
            {primaryContent}
          </p>
        </div>

        {/* Expand indicator */}
        {hasExpandableContent && (
          <ChevronDown
            className={`w-3.5 h-3.5 text-text-muted shrink-0 mt-1 transition-transform duration-200 ${
              expanded ? "rotate-180" : ""
            }`}
          />
        )}
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-3 pl-11">
              <ExpandedContent payload={payload} type={msg.type} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Inline HITL response */}
      {isInputRequest && (
        <InlineInputResponse msg={msg} />
      )}
    </div>
  );
}

function ExpandedContent({ payload, type }: { payload: any; type: string }) {
  if (type === "error" && payload.detail) {
    return (
      <pre className="text-xs text-brand-pink/70 font-mono bg-brand-pink/5 rounded-lg p-3 overflow-x-auto">
        {payload.detail}
      </pre>
    );
  }

  if (type === "decision" && payload.alternatives) {
    return (
      <div className="space-y-1.5">
        {payload.rationale && (
          <p className="text-xs text-text-muted italic">{payload.rationale}</p>
        )}
        <div className="text-[11px] text-text-muted font-medium">Alternatives:</div>
        {payload.alternatives.map((alt: string, i: number) => (
          <div
            key={i}
            className="text-xs text-text-secondary bg-surface/20 rounded px-2.5 py-1.5"
          >
            {alt}
          </div>
        ))}
      </div>
    );
  }

  if (type === "completion" && payload.data) {
    return (
      <pre className="text-xs text-text-secondary font-mono bg-surface/20 rounded-lg p-3 overflow-x-auto max-h-48">
        {typeof payload.data === "string"
          ? payload.data
          : JSON.stringify(payload.data, null, 2)}
      </pre>
    );
  }

  if (type === "artifact" && payload.content) {
    return (
      <div>
        {payload.mimeType && (
          <span className="text-[10px] text-text-muted font-mono mb-1 block">
            {payload.mimeType}
            {payload.sizeBytes ? ` (${formatBytes(payload.sizeBytes)})` : ""}
          </span>
        )}
        <pre className="text-xs text-text-secondary font-mono bg-surface/20 rounded-lg p-3 overflow-x-auto max-h-48">
          {typeof payload.content === "string"
            ? payload.content.slice(0, 2000)
            : JSON.stringify(payload.content, null, 2).slice(0, 2000)}
        </pre>
      </div>
    );
  }

  // Generic JSON fallback
  const filtered = Object.fromEntries(
    Object.entries(payload).filter(
      ([k]) => !["message", "summary", "decision", "observation", "question", "subject"].includes(k),
    ),
  );
  if (Object.keys(filtered).length === 0) return null;

  return (
    <pre className="text-xs text-text-muted font-mono bg-surface/20 rounded-lg p-3 overflow-x-auto max-h-48">
      {JSON.stringify(filtered, null, 2)}
    </pre>
  );
}

function InlineInputResponse({ msg }: { msg: FeedbackItem }) {
  const respond = useRespondToInput();
  const removePendingInput = useFeedbackStore((s) => s.removePendingInput);
  const [textValue, setTextValue] = useState("");
  const [responded, setResponded] = useState(false);

  const payload = msg.payload ?? {};

  function handleRespond(value: unknown) {
    respond.mutate({
      runId: payload.runId,
      body: {
        feedbackMessageId: msg.id,
        value,
      },
    });
    removePendingInput(msg.id);
    setResponded(true);
  }

  if (responded) {
    return (
      <div className="px-4 pb-3 pl-11">
        <span className="text-xs text-brand-green">Responded</span>
      </div>
    );
  }

  return (
    <div className="px-4 pb-3 pl-11">
      {payload.inputType === "confirmation" && (
        <div className="flex gap-2">
          <button
            onClick={() => handleRespond(true)}
            className="bg-brand-green/10 text-brand-green hover:bg-brand-green/20 px-3 py-1 rounded-lg text-xs font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
          >
            Approve
          </button>
          <button
            onClick={() => handleRespond(false)}
            className="bg-brand-pink/10 text-brand-pink hover:bg-brand-pink/20 px-3 py-1 rounded-lg text-xs font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
          >
            Reject
          </button>
        </div>
      )}

      {payload.inputType === "choice" && (
        <div className="flex flex-wrap gap-1.5">
          {(payload.options ?? []).map((opt: any) => (
            <button
              key={opt.value}
              onClick={() => handleRespond(opt.value)}
              className="bg-surface/30 hover:bg-brand-purple/10 hover:text-brand-purple rounded-lg px-3 py-1 text-xs text-text-secondary transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}

      {(payload.inputType === "text" || !payload.inputType) && (
        <div className="flex gap-2">
          <input
            type="text"
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
            placeholder="Type response..."
            className="flex-1 bg-surface/30 border border-border-subtle/20 rounded-lg px-3 py-1 text-xs text-text-primary placeholder-text-muted focus:outline-none focus:border-brand-purple/30"
            onKeyDown={(e) => {
              if (e.key === "Enter" && textValue.trim()) {
                handleRespond(textValue.trim());
              }
            }}
          />
          <button
            onClick={() => textValue.trim() && handleRespond(textValue.trim())}
            className="bg-brand-purple/10 text-brand-purple hover:bg-brand-purple/20 px-3 py-1 rounded-lg text-xs font-medium transition-[color,background-color,transform] duration-200 active:scale-[0.96]"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──

function extractPrimaryContent(payload: any, type: string): string {
  if (payload.message) return payload.message;
  if (payload.summary) return payload.summary;
  if (payload.decision) return payload.decision;
  if (payload.observation) return payload.observation;
  if (payload.question) return payload.question;
  if (payload.name) return payload.name;
  if (payload.subject)
    return `${payload.subject}: ${Math.round((payload.score ?? 0) * 100)}%`;
  return JSON.stringify(payload).slice(0, 120);
}

function hasMoreContent(payload: any, type: string): boolean {
  if (type === "error" && payload.detail) return true;
  if (type === "decision" && (payload.alternatives || payload.rationale)) return true;
  if (type === "completion" && payload.data) return true;
  if (type === "artifact" && payload.content) return true;
  const keys = Object.keys(payload).filter(
    (k) => !["message", "summary", "decision", "observation", "question", "subject", "score", "name", "inputType", "options", "runId"].includes(k),
  );
  return keys.length > 0;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes}B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)}MB`;
}
